import React from 'react';
import PropTypes from 'prop-types';
import { compact } from 'lodash-es';
import { MarkdownImage } from 'site-modules/canvas/components/chat-messages/ai-message/markdown/markdown-image/markdown-image';
import { AnimationSchedule } from 'site-modules/canvas/components/animation-schedule/animation-schedule';
import { WordByWordRenderer } from 'site-modules/canvas/components/chat-messages/ai-message/markdown/word-by-word-renderer/word-by-word-renderer';
import { MarkdownPhotosCarousel } from './markdown-photos-carousel/markdown-photos-carousel';

export function MarkdownList({
  children,
  listType = 'ul',
  start = undefined,
  reversed = false,
  type = '1',
  unitId = 'list',
  isAnimationDisabled = false,
  ...restProps
}) {
  const photosChildren = compact(
    React.Children.toArray(children).map(child => {
      if (typeof child === 'string') {
        return child.trim();
      }

      return child;
    })
  ).map(child => child?.props?.children);
  const isPhotosCarousel = photosChildren.every(child => child?.type === MarkdownImage || child?.type === 'img');

  const ListTag = listType;
  const listAttributes = listType === 'ol' ? { start, reversed, type } : {};

  return isPhotosCarousel ? (
    <AnimationSchedule.MarkdownScheduledNode
      as={MarkdownPhotosCarousel}
      unitId={`${unitId}:photos`}
      isAnimationDisabled={isAnimationDisabled}
      reportAsRevealLeaf
    >
      {photosChildren}
    </AnimationSchedule.MarkdownScheduledNode>
  ) : (
    <ListTag {...restProps} {...listAttributes}>
      {children}
    </ListTag>
  );
}

export function MarkdownListItem({ children = null, unitId, isAnimationDisabled = false, ...restProps }) {
  const animatedChildren = React.Children.map(children, (child, childIndex) => {
    if (typeof child !== 'string' || !child.trim()) {
      return child;
    }

    return (
      <WordByWordRenderer
        tag={React.Fragment}
        unitId={`${unitId}:content:${childIndex}`}
        isAnimationDisabled={isAnimationDisabled}
      >
        {child}
      </WordByWordRenderer>
    );
  });

  return (
    <AnimationSchedule.MarkdownScheduledNode
      {...restProps}
      as="li"
      unitId={unitId}
      isAnimationDisabled={isAnimationDisabled}
    >
      {animatedChildren}
    </AnimationSchedule.MarkdownScheduledNode>
  );
}

MarkdownListItem.displayName = 'MarkdownListItem';

MarkdownList.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  listType: PropTypes.oneOf(['ul', 'ol']),
  start: PropTypes.number,
  reversed: PropTypes.bool,
  type: PropTypes.string,
  unitId: PropTypes.string,
  isAnimationDisabled: PropTypes.bool,
};

MarkdownListItem.propTypes = {
  children: PropTypes.node,
  unitId: PropTypes.string.isRequired,
  isAnimationDisabled: PropTypes.bool,
};
